import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";
import DeliveryMap from "../Map/DeliveryMap";
import { FaMotorcycle, FaMapMarkerAlt, FaCircleNotch, FaArrowLeft, FaUserTie, FaTimes } from "react-icons/fa";

const OrderTracking = () => {
  const navigate = useNavigate();
  const { orderId: paramOrderId } = useParams();
  
  const [searchInput, setSearchInput] = useState(paramOrderId || "");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  
  const [trackedItems, setTrackedItems] = useState([]);
  const [baseUrl] = useState(window.API_URL || "");
  
  const socketRef = useRef(null);

  // Fetch Delivery Persons on Mount
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/delivery-employees`);
        if (response.data && Array.isArray(response.data)) {
          setDeliveryPersons(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch drivers:", err);
      }
    };
    fetchDrivers();
  }, [baseUrl]);

  // Setup Socket
  useEffect(() => {
    const socketUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const socket = io(socketUrl);
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected for tracking");
    });

    socket.on("location_changed", (data) => {
      if (data.lat && data.lng) {
        setTrackedItems(prev => prev.map(item => {
           // If the backend sends orderId, we can strictly match.
           // Otherwise, we update all items. Usually the backend room handles separation.
           if (data.orderId && item.trueOrderId !== data.orderId) {
             return item; 
           }
           return {
             ...item,
             driverLocation: { lat: data.lat, lng: data.lng },
             orderStatus: "Driver is on the way!"
           };
        }));
      }
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    return () => {
      socket.disconnect();
    };
  }, [baseUrl]);

  const fetchOrderDetails = async (id, isDriverAssigned = false, driverName = "") => {
    try {
      const searchId = id.replace(/^#/, ''); // Remove # if user pasted it
      
      // Check if already tracking
      if (trackedItems.some(item => item.displayId === searchId)) {
         return; // Already tracking
      }

      const response = await fetch(`${baseUrl}/api/track-order/${searchId}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        if (socketRef.current) {
          socketRef.current.emit("join_order_room", { orderId: data.orderId });
        }
        
        let formattedAddress = "";
        if (data.deliveryAddress) {
          const addr = data.deliveryAddress;
          if (typeof addr === "object") {
            formattedAddress = [
              addr.flat_villa_no, addr.building_name, addr.field3,
              addr.field2, addr.field1, addr.country
            ].filter(Boolean).join(", ");
          } else {
            formattedAddress = addr;
          }
        }
        
        const newItem = {
           id: Date.now() + Math.random(),
           displayId: searchId,
           trueOrderId: data.orderId,
           deliveryAddress: formattedAddress,
           driverLocation: (data.pickupLocation && data.pickupLocation.lat && data.pickupLocation.lng) 
              ? { lat: data.pickupLocation.lat, lng: data.pickupLocation.lng } 
              : null,
           orderStatus: (data.pickupLocation && data.pickupLocation.lat && data.pickupLocation.lng)
              ? "Driver is on the way!" 
              : "Waiting for driver location...",
           title: isDriverAssigned ? `Driver: ${driverName} (Order: #${searchId})` : `Order: #${searchId}`
        };

        setTrackedItems(prev => [newItem, ...prev]);
      } else {
        alert(data.message || `Order ${searchId} not found or not active.`);
      }
    } catch (err) {
      console.error("Failed to fetch order details:", err);
      alert("Error connecting to server.");
    }
  };

  const handleTrackOrder = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      fetchOrderDetails(searchInput.trim());
      setSearchInput("");
    }
  };

  const handleTrackDriver = async (e) => {
    e.preventDefault();
    if (!selectedDriver) return;
    
    // Find driver name
    const driver = deliveryPersons.find(d => d.employeeId?.toString() === selectedDriver.toString());
    const driverName = driver ? driver.name : "Driver";

    // Fetch active orders to find which ones belong to this driver and are picked_up
    try {
      const response = await axios.get(`${baseUrl}/api/activeorders`);
      const orders = response.data;
      
      if (Array.isArray(orders)) {
         const driverOrders = orders.filter(o => 
           o.deliveryPersonId?.toString() === selectedDriver.toString() && 
           o.status === "picked_up"
         );

         if (driverOrders.length === 0) {
            alert(`No active picked-up orders found for ${driverName}.`);
            return;
         }

         // Fetch details for each order to add to tracking
         driverOrders.forEach(o => {
            fetchOrderDetails(o.orderNo || o.orderId, true, driverName);
         });
      }
    } catch (err) {
      console.error("Failed to fetch active orders for driver:", err);
    }
    
    setSelectedDriver("");
  };

  const removeTrackedItem = (id) => {
     setTrackedItems(prev => prev.filter(item => item.id !== id));
  };

  // If paramOrderId exists initially, track it
  useEffect(() => {
    if (paramOrderId) {
      fetchOrderDetails(paramOrderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      
      {/* Header and Controls */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
          
          {/* Left Corner: Back Button */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <button 
              onClick={() => navigate('/home')}
              style={{ 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                cursor: 'pointer', 
                color: '#3b82f6',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: '8px 20px',
                borderRadius: '24px', 
                transition: 'all 0.2s ease',
                fontWeight: '600',
                fontSize: '0.9rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              <FaArrowLeft size={14} /> <span style={{ marginLeft: '6px' }}>Back</span>
            </button>
          </div>
          
          {/* Center: Live Tracking Heading */}
          <div style={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.6rem', fontWeight: '700', whiteSpace: 'nowrap', letterSpacing: '-0.5px' }}>
              Multi-Order Live Tracking
            </h2>
          </div>

          {/* Right Corner: Empty Spacer for Center Alignment */}
          <div style={{ flex: 1 }}></div>

        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
           {/* Track by Order ID */}
           <form onSubmit={handleTrackOrder} style={{ flex: '1 1 300px', display: 'flex', gap: '10px' }}>
             <input 
               type="text" 
               placeholder="Enter Order ID to track..." 
               value={searchInput}
               onChange={(e) => setSearchInput(e.target.value)}
               style={{ flex: 1, padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none' }}
             />
             <button type="submit" style={{ padding: '12px 24px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}>
               Track Order
             </button>
           </form>

           {/* Track by Delivery Person */}
           <form onSubmit={handleTrackDriver} style={{ flex: '1 1 300px', display: 'flex', gap: '10px' }}>
             <select 
               value={selectedDriver}
               onChange={(e) => setSelectedDriver(e.target.value)}
               style={{ flex: 1, padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', backgroundColor: '#fff' }}
             >
               <option value="">Select Delivery Person...</option>
               {deliveryPersons.map(dp => (
                  <option key={dp.employeeId} value={dp.employeeId}>{dp.name}</option>
               ))}
             </select>
             <button type="submit" style={{ padding: '12px 24px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}>
               Track Person
             </button>
           </form>
        </div>
      </div>

      {/* Tracked Items Grid */}
      {trackedItems.length === 0 ? (
         <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#64748b', border: '2px dashed #cbd5e1' }}>
            <FaMotorcycle size={48} style={{ color: '#cbd5e1', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#334155' }}>No active tracking</h3>
            <p style={{ margin: 0 }}>Enter an Order ID or select a Delivery Person to start live tracking.</p>
         </div>
      ) : (
         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
            {trackedItems.map((item) => (
               <div key={item.id} style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  
                  {/* Card Header */}
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                     <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.title.includes('Driver') ? <FaUserTie style={{color: '#10b981'}}/> : <FaMotorcycle style={{color: '#3b82f6'}}/>}
                        {item.title}
                     </h3>
                     <button 
                        onClick={() => removeTrackedItem(item.id)}
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                        title="Stop tracking"
                        onMouseOver={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                     >
                        <FaTimes size={16} />
                     </button>
                  </div>

                  {/* Status Bar */}
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: '#475569', fontWeight: '500' }}>
                     {!item.driverLocation ? (
                        <FaCircleNotch className="fa-spin" style={{ color: '#3b82f6' }} />
                     ) : (
                        <FaMapMarkerAlt style={{ color: '#10b981' }} />
                     )}
                     {item.orderStatus}
                  </div>

                  {/* Map Area */}
                  <div style={{ height: '350px', position: 'relative', backgroundColor: '#f1f5f9' }}>
                     {item.driverLocation ? (
                        <DeliveryMap driverLocation={item.driverLocation} address={item.deliveryAddress} height="350px" />
                     ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', padding: '20px', textAlign: 'center' }}>
                           <p>Map will appear once the driver starts moving and location is received.</p>
                        </div>
                     )}
                  </div>
               </div>
            ))}
         </div>
      )}

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .fa-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default OrderTracking;
